import { State } from '@progress/kendo-data-query';
import { useCallback, useState } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/assets/static/ApiRoutes';
import useAsync from 'src/hooks/useAsync';
import { actionRequiredFilters } from 'src/sections/action_required_invoices/actionRequiredFilters';
import axiosInstance from 'src/utils/axios';
import { removeTime } from 'src/utils/formatTime';
import { modifyRequest } from 'src/utils/requestInterceptor';

export interface IGlSummary {
  rowId?: string;
  glCd?: string;
  description?: string;
  poGlTotal?: number;
  budgetPeriod?: string;
  currentDocGlTotal?: number;
  otherDocGlTotal?: number;
  glBalance?: number;
  id: number;
  expanded?: boolean;
  items?: IGlSummaryGropued[];
  invoiceType?: string;
  [key: string]: any;
  budgetPeriodEndDate: string | Date | null;
  budgetPeriodStartDate: string | Date | null;
  glPeriodId?: number;
  glCdId?: number;
  accountingPeriod?: string;
  fiscalYear?: number;
  glRemainingInitialBudget?: number;
  glRemainingBudget?: number;
  periodNumber?: number;
}

export interface IGlSummaryGropued {
  rowId?: string;
  glCd?: string;
  description?: string;
  poGlTotal?: number;
  budgetPeriod?: string;
  currentDocGlTotal?: number;
  otherDocGlTotal?: number;
  glBalance?: number;
  id?: number;
  expanded?: boolean;
  [key: string]: any;
  budgetPeriodEndDate: string | Date | null;
  budgetPeriodStartDate: string | Date | null;
  glRemainingInitialBudget?: number;
  glRemainingBudget?: number;
}

export interface IItemsActionRequired {
  rowId?: string;
  selected?: boolean;
  receiptId?: number;
  invoiceNumber?: string;
  status?: string;
  docType?: string;
  companyName?: string;
  buyerName?: string;
  supplierName?: string;
  totalCostAmt?: number;
  poTotal?: number;
  poId?: number;
  statusDate?: string;
  invoiceDate?: string | Date;
  numOfAttachments?: number;
  glSummaries: IGlSummary[];
  percentRecvByDollar?: number | string;
  percentRecvByQuantity?: number | string;
  hasGlMismatchItems?: boolean | 'Y' | 'N';
  poItemsCompleted?: boolean | 'Y' | 'N';
  canApproveInvoice?: boolean;
  requireFurtherApproval?: boolean;
  canSubmitInvoiceForApproval?: boolean;
  canCompleteInvoice?: boolean;
  canRecallInvoice?: boolean;
  canDeclineInvoice?: boolean;
  invoiceOverCreatorLimit?: boolean;
  invoiceGlMismatch?: boolean;
  invoiceContainsNewLineItem?: boolean;
  invoiceItemQtyOverage?: boolean;
  invoiceItemPriceOverage?: boolean;
  invoiceOverPoDollarLimit?: boolean;
  invoiceOverPoPercentLimit?: boolean;
  requesterInvoiceOverCreatorLimit?: boolean;
  requesterInvoiceGlMismatch?: boolean;
  requesterInvoiceContainsNewLineItem?: boolean;
  requesterInvoiceItemQtyOverage?: boolean;
  requesterInvoiceItemPriceOverage?: boolean;
  requesterInvoiceOverPoDollarLimit?: boolean;
  requesterInvoiceOverPoPercentLimit?: boolean;
  buyerNotes?: string;
  budgetPeriodStartDate?: string;
  budgetPeriodEndDate?: string;
  isMultiPeriod: boolean;
  isMpa: boolean;
  fiscalYear?: number;
}

export interface IActionRequiredResponse {
  totalRecords: number;
  items: IItemsActionRequired[];
}

const dateArr = ['statusDate', 'invoiceDate'];

interface ActionRequiredFilterType {
  value: number;
  label: string;
}

export interface IActionRequiredInvoiceApiProps {
  actionRequiredSelectionFilter?: ActionRequiredFilterType;
  actionRequiredDataState?: State;
}

const useActionRequiredInvoicesApi = () => {
  const [actionRequiredGLSummaries, setActionRequiredGLSummaries] = useState<IGlSummary[]>([]);

  const call = useCallback(async (actionRequiredInvoiceParams?: IActionRequiredInvoiceApiProps) => {
    const { actionRequiredSelectionFilter = { value: 0 }, actionRequiredDataState = {} } =
      actionRequiredInvoiceParams || {};
    try {
      const finalFilters = { ...modifyRequest(actionRequiredDataState, dateArr) };
      finalFilters.filters = [
        ...finalFilters.filters,
        ...(actionRequiredFilters[actionRequiredSelectionFilter.value]?.filters || []),
      ];
      finalFilters.sort = [
        ...finalFilters.sort,
        ...(actionRequiredFilters[actionRequiredSelectionFilter.value]?.sort || []),
      ];
      const res = await axiosInstance.post<IActionRequiredResponse>(
        api.actionRequiredInvoice(actionRequiredSelectionFilter.value === 3),
        finalFilters
      );
      const data: IActionRequiredResponse = res?.data;
      data.items = data?.items?.map((item) => ({
        ...item,
        rowId: uuid(),
        invoiceDate: removeTime(new Date(item.invoiceDate as string)),
        glSummaries: item?.glSummaries?.map((glSummary) => ({ rowId: uuid(), ...glSummary })),
        isMultiPeriod: item?.isMpa,
      }));
      const flattenedGLSummaries = data?.items?.flatMap((item) => item.glSummaries || []);
      setActionRequiredGLSummaries(flattenedGLSummaries);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  }, []);

  const {
    value: { items, totalRecords },
    setValue,
    execute,
    isLoading,
  } = useAsync<IActionRequiredResponse, IActionRequiredInvoiceApiProps>(
    call,
    false,
    undefined,
    {} as IActionRequiredResponse
  );

  return {
    totalActionRequiredInvoices: totalRecords || (0 as number),
    actionRequiredItems: (items || []) as IItemsActionRequired[],
    setActionRequiredInvoice: setValue,
    getActionRequiredInvoice: execute,
    actionRequiredLoading: isLoading,
    actionRequiredGLSummaries,
  };
};

export default useActionRequiredInvoicesApi;
